import { Box, Button, Grid, Typography, Card, CircularProgress, Divider, Paper } from "@mui/material";
import { ProcessExecutionResponse, StepExecutionResponse, StripePaymentContent } from "api/main/data-contracts";
import { useEffect, useRef, useState } from "react";
import { createApi } from "util/ApiCreator";
import { adminTheme } from "theme/adminTheme";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

interface Props {
  process: ProcessExecutionResponse;
  step: StepExecutionResponse;
  execute: (obj: object) => Promise<void>;
  refresh: () => Promise<void>;
  previous: () => void;
  next: () => void;
  canGoNext: boolean;
  canGoBack: boolean;
}

export default function StripePaymentExecution({
  process,
  step,
  execute,
  refresh,
  next,
  previous,
  canGoBack,
  canGoNext
}: Props) {
  const stripePaymentApi = useRef(createApi("stripepayment"));
  const [loading, setLoading] = useState<boolean>(false);
  const [paymentStatus, setPaymentStatus] = useState<string>(
    step.executionInfo?.stripePaymentInfo?.status ?? "pending"
  );

  const stripePaymentContent: StripePaymentContent = getStripePaymentContent();

  function getStripePaymentContent(): StripePaymentContent {
    if (!step.step?.stepInformation?.stripePaymentContent) {
      return {
        amount: 0,
        currency: "EUR",
        description: "",
        paymentType: "ONE_TIME",
      };
    }
    return step.step.stepInformation.stripePaymentContent;
  }

  const isCompleted = paymentStatus === "completed";
  const isStepActive = step.status === "FINISHED";

  // Check for Stripe session result from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");

    if (sessionId && step.id) {
      checkStripePaymentStatus(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkStripePaymentStatus = async (sessionId: string) => {
    if (!step.id) return;

    setLoading(true);
    try {
      const status = await stripePaymentApi.current.getPaymentStatus(step.id, sessionId);
      setPaymentStatus(status);
      await refresh();
    } catch (error) {
      console.error("Error checking payment status:", error);
    }
    setLoading(false);
  };

  const handlePaymentButtonClick = async () => {
    if (!process.id || !step.id) return;

    setLoading(true);
    try {
      const checkoutUrl = await stripePaymentApi.current.initializePayment(process.id, step.id);
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      }
    } catch (error) {
      console.error("Error initializing Stripe payment:", error);
    }
    setLoading(false);
  };

  const handleExecuteButtonClick = async () => {
    if (paymentStatus !== "completed" && paymentStatus !== "pending") {
      return;
    }
    await execute({ stripePaymentContent }).catch((err: any) => {
      console.error("Error executing step:", err);
    });
    await refresh();
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 5 }}>
        <CircularProgress size={64} sx={{ color: "#6772E5", mb: 3 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary }}>
          Prašome palaukti
        </Typography>
        <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary }}>
          Apdorojama...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, height: "100%" }}>
      <Card
        sx={{
          borderRadius: adminTheme.borderRadius.lg,
          border: `1px solid ${adminTheme.colors.border}`,
          boxShadow: "none",
          overflow: "hidden",
        }}
      >
        {/* Card Header */}
        <Box
          sx={{
            p: 3,
            background: "linear-gradient(135deg, #6772E508 0%, #f8f9fa 100%)",
            borderBottom: `1px solid ${adminTheme.colors.border}`,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: adminTheme.borderRadius.md,
              background: "linear-gradient(135deg, #6772E5 0%, #7B85EB 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            <CreditCardIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: adminTheme.colors.textPrimary }}>
              Stripe mokėjimas
            </Typography>
            <Typography variant="body2" sx={{ color: adminTheme.colors.textSecondary }}>
              Saugus mokėjimas per Stripe
            </Typography>
          </Box>
          {isCompleted && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1,
                bgcolor: `${adminTheme.colors.success}15`,
                borderRadius: adminTheme.borderRadius.md,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 20, color: adminTheme.colors.success }} />
              <Typography variant="body2" sx={{ color: adminTheme.colors.success, fontWeight: 600 }}>
                Apmokėta
              </Typography>
            </Box>
          )}
        </Box>

        {/* Card Content */}
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Suma
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#6772E5", mt: 0.5 }}>
                  {stripePaymentContent.amount} {stripePaymentContent.currency}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Tipas
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: adminTheme.colors.textPrimary, mt: 0.5 }}>
                  {stripePaymentContent.paymentType === "ONE_TIME" ? "Vienkartinis mokėjimas" : "Prenumerata"}
                </Typography>
              </Box>
            </Grid>
            {stripePaymentContent.description && (
              <>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Aprašymas
                    </Typography>
                    <Typography variant="body2" sx={{ color: adminTheme.colors.textPrimary, mt: 0.5 }}>
                      {stripePaymentContent.description}
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}
          </Grid>

          {/* Error Message */}
          {paymentStatus === "failed" && (
            <Paper
              sx={{
                mt: 3,
                p: 2,
                bgcolor: `${adminTheme.colors.error}10`,
                border: `1px solid ${adminTheme.colors.error}40`,
                borderRadius: adminTheme.borderRadius.md,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <ErrorOutlineIcon sx={{ color: adminTheme.colors.error, fontSize: 24 }} />
              <Typography variant="body2" sx={{ color: adminTheme.colors.error, fontWeight: 600 }}>
                Mokėjimas nepavyko. Bandykite dar kartą.
              </Typography>
            </Paper>
          )}

          {/* Payment Button */}
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handlePaymentButtonClick}
            disabled={isCompleted || loading}
            sx={{
              mt: 3,
              background: "linear-gradient(135deg, #6772E5 0%, #7B85EB 100%)",
              "&:hover": { background: "linear-gradient(135deg, #5A67D8 0%, #6B7AE8 100%)" },
              py: 1.5,
              fontWeight: 600,
            }}
          >
            {isCompleted ? "Jau apmokėta" : "Mokėti su Stripe"}
          </Button>
        </Box>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", gap: 2, mt: "auto" }}>
        <Button
          variant="outlined"
          size="large"
          disabled={!canGoBack}
          onClick={() => previous()}
          sx={{
            borderColor: adminTheme.colors.border,
            color: adminTheme.colors.textSecondary,
            py: 1.5,
            "&:hover": {
              borderColor: adminTheme.colors.primary,
              bgcolor: `${adminTheme.colors.primary}08`,
            },
          }}
        >
          Ankstesnis
        </Button>
        <Button
          variant="outlined"
          size="large"
          disabled={isStepActive}
          onClick={() => handleExecuteButtonClick()}
          sx={{
            flex: 1,
            borderColor: adminTheme.colors.border,
            color: adminTheme.colors.textSecondary,
            py: 1.5,
            "&:hover": {
              borderColor: adminTheme.colors.primary,
              bgcolor: `${adminTheme.colors.primary}08`,
            },
          }}
        >
          Patvirtinti mokėjimą
        </Button>
        <Button
          variant="contained"
          size="large"
          disabled={!isCompleted}
          onClick={() => next()}
          sx={{
            flex: 1,
            background: adminTheme.gradients.success,
            "&:hover": { opacity: 0.9 },
            py: 1.5,
            fontWeight: 600,
          }}
        >
          Kitas
        </Button>
      </Box>
    </Box>
  );
}
